/**
 * Wire-format helpers for TTS Flow. The backend payload is several
 * JSON-stringified blobs glued together; these functions hold the
 * stringification rules and the inverse parser used to hydrate the
 * wizard for Update/Clone. Wire output must remain byte-identical to
 * legacy obd_frontend.
 */
import type {
  Gender,
  TtsFlowComposeRequest,
  TtsFlowDetailResponse,
  TtsFlowFormValues,
  TtsFlowUpdateRequest,
  TtsPatchRow,
  TtsRow,
  TtsTemplateId,
} from './types';

/** Wrap a pasted SMS gateway URL in the legacy `{url,smsText,requestType}`
 *  JSON envelope. Webhook-picker values already arrive as JSON strings, so
 *  if the value already parses as a `{ url: ... }` object we pass it
 *  through unchanged. Empty strings stay empty. */
function wrapSmsApi(rawOrJson: string): string {
  if (!rawOrJson) return '';
  try {
    const parsed = JSON.parse(rawOrJson);
    if (parsed && typeof parsed === 'object' && 'url' in parsed) {
      return rawOrJson;
    }
  } catch {
    /* fall through — pasted URL */
  }
  return JSON.stringify({
    url: rawOrJson,
    smsText: '',
    requestType: 'GET',
  });
}

/** Build the wire-format compose body from the form values. */
export function buildTtsFlowRequest(input: {
  userId: number | string;
  values: TtsFlowFormValues;
}): TtsFlowComposeRequest {
  const { userId, values: v } = input;

  // Strip editor-only fields (`text`, `previewUrl`, `previewTtsId`) before
  // serializing — the dialplan only knows about `{ttsType, value,
  // variableType}`. Any text-mode rows that reach this point still carrying
  // a `text` field would normally have been auto-synthesized into prompt
  // rows by the wizard's submit handler; if not, we drop them rather than
  // send a row the backend can't interpret.
  const cleanRows = v.ttsRows
    .filter((r) => !(r.text && r.text.length > 0 && r.ttsType !== 'prompt'))
    .map((r) => ({
      ttsType: r.ttsType,
      value: r.value,
      variableType: r.variableType,
    }));
  const ttsFlow = JSON.stringify({
    language: v.language,
    gender: v.gender,
    // Legacy nests ttsList one level deeper: `{ ttsList: [...] }`.
    ttsRows: { ttsList: cleanRows },
  });

  return {
    userId,
    templateId: (v.templateId || '') as TtsTemplateId | '',
    ttsFlowName: v.ttsFlowName,
    ttsFlow,
  };
}

/** Build the update body — same as compose plus the `id`. */
export function buildTtsFlowUpdateRequest(input: {
  userId: number | string;
  values: TtsFlowFormValues;
  id: number;
}): TtsFlowUpdateRequest {
  return {
    ...buildTtsFlowRequest(input),
    id: input.id,
  };
}

/* ---------------- Hydration (server response → form values) ---------------- */

function safeParse<T>(s: string | undefined | null, fallback: T): T {
  if (!s) return fallback;
  try {
    return JSON.parse(s) as T;
  } catch {
    return fallback;
  }
}

interface ParsedTtsFlow {
  language?: string;
  gender?: string;
  ttsRows?: { ttsList?: TtsRow[] } | TtsRow[];
}

interface ParsedTemplateData {
  dtmf?: string;
  welcomePId?: string;
  menuPId?: string;
  noInputPId?: string;
  wrongInputPId?: string;
  thanksPId?: string;
  menuWaitTime?: string;
  rePrompt?: string;
  noAgentId?: string;
}

interface ParsedSmsData {
  smsSuccessApi?: string;
  smsFailApi?: string;
  smsDtmfApi?: string;
  callDurationSMS?: number | string;
  webhook?: boolean | string;
  webhookId?: string;
  callPatchSuccessMessage?: string;
  callPatchFailMessage?: string;
}

interface ParsedAgentRows {
  patchList?: TtsPatchRow[];
}

/** Pulls the `url` field out of the wrapped SMS-api JSON envelope. When the
 *  user originally picked a webhook, the value is itself a webhook payload
 *  with a `url` field — we hand the whole JSON back so the form can still
 *  identify the webhook by exact-string match. */
function unwrapSmsApi(raw: string | undefined): string {
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'url' in parsed) {
      // For pasted URLs we wrapped with smsText='' & requestType='GET' — strip
      // the wrapper and hand back the bare URL. Anything richer (webhook
      // payload) is preserved as a JSON string for round-tripping.
      if (
        'smsText' in parsed &&
        'requestType' in parsed &&
        !('payload' in parsed) &&
        !('header' in parsed) &&
        parsed.smsText === '' &&
        parsed.requestType === 'GET'
      ) {
        return String((parsed as { url?: string }).url ?? '');
      }
      return raw;
    }
  } catch {
    /* not JSON — bare URL? Pass through. */
  }
  return raw;
}

/** Hydrate the wizard's form values from a server detail response. */
export function hydrateTtsFlowFromDetail(res: TtsFlowDetailResponse): Partial<TtsFlowFormValues> {
  const ttsFlow = safeParse<ParsedTtsFlow>(res.ttsFlow, {});

  // The legacy nests TTS rows two levels deep: `ttsFlow.ttsRows.ttsList`.
  // Older payloads might have a flat array — handle both.
  const ttsRows: TtsRow[] = Array.isArray(ttsFlow.ttsRows)
    ? ttsFlow.ttsRows
    : (ttsFlow.ttsRows?.ttsList ?? []);

  return {
    templateId: (res.templateId || '') as TtsTemplateId | '',
    ttsFlowName: res.ttsFlowName ?? '',
    language: ttsFlow.language ?? '',
    gender: (ttsFlow.gender ?? '') as Gender | '',
    ttsRows,
  };
}
